import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DailyLog from "@/pages/DailyLog";
import FollowUpsSheet from "@/pages/FollowUpsSheet";
import ReviewsSheet from "@/pages/ReviewsSheet";
import LabWorkSheet from "@/pages/LabWorkSheet";
import CompletedSheet from "@/pages/CompletedSheet";
import IncompleteSheet from "@/pages/IncompleteSheet";
import EntryDetail from "@/pages/EntryDetail";
import MessageTemplates from "@/pages/MessageTemplates";
import PatientResources from "@/pages/PatientResources";
import AdminUsers from "@/pages/AdminUsers";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <BrandSplash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function OwnerRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <BrandSplash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <BrandSplash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function BrandSplash() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4"
         data-testid="brand-splash">
      <img
        src="/clinic-logo.webp"
        alt="Lucid Dental"
        className="h-10 w-auto animate-pulse"
      />
      <div className="text-xs uppercase tracking-widest text-slate-400">Follow-up Tracker</div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/daily-log" element={<ProtectedRoute><DailyLog /></ProtectedRoute>} />
      <Route path="/follow-ups" element={<ProtectedRoute><FollowUpsSheet /></ProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute><ReviewsSheet /></ProtectedRoute>} />
      <Route path="/lab-work" element={<ProtectedRoute><LabWorkSheet /></ProtectedRoute>} />
      <Route path="/incomplete" element={<ProtectedRoute><IncompleteSheet /></ProtectedRoute>} />
      <Route path="/completed" element={<ProtectedRoute><CompletedSheet /></ProtectedRoute>} />
      <Route path="/message-templates" element={<ProtectedRoute><MessageTemplates /></ProtectedRoute>} />
      <Route path="/patient-resources" element={<ProtectedRoute><PatientResources /></ProtectedRoute>} />
      <Route path="/entries/:id" element={<ProtectedRoute><EntryDetail /></ProtectedRoute>} />
      <Route path="/users" element={<OwnerRoute><AdminUsers /></OwnerRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
